/* eslint-disable react-native-enforce-testid/testid-missing */
import {
  useNavigation,
  useFocusEffect,
  useIsFocused,
} from "@react-navigation/native";
import React, { memo, useCallback, useEffect, useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  SectionList,
  Image,
  ActivityIndicator,
  Linking,
  TouchableWithoutFeedback,
  TouchableNativeFeedback,
  Platform,
  Pressable,
  Alert,
  BackHandler,
} from "react-native";

import debounce from "lodash/debounce";

import t from "@constants/localization";
import colors from "@constants/color";
import analytics from "@services/analytics";
import {
  RRD_EVENTS,
  SEARCH_EVENTS,
  eventActionMapping,
} from "@constants/analyticsEventsCT";
import CallIcon from "@assets/svg/newCall";
import Tick from "@assets/svg/MyDeliveries/Tick";
import {
  markDelivery,
  markPickup,
  cancelPickupReq,
  returnRequestCancelV2,
  unMarkDelivery,
  unMarkPickup,
  rescheduleDelivery,
} from "@services/api";
import Location from "@assets/images/LocationG.png";
import Carousel from "@components/RRD/Carousel";
import Cash from "@assets/svg/RRD/Cash";
import Button from "@components/RRD/Button";
import ToastMessage from "@components/Toast";
import { useSelector, useDispatch } from "react-redux";
import RRDStatus from "@assets/svg/RRDStatus";
import ChevronRight from "@assets/svg/ChevronRight";
import Clock from "@assets/svg/RRD/Clock";
import Backward from "@assets/svg/RRD/Backward";
import Dot from "@assets/svg/Dot";
import DonutChart from "@components/DonutChart";
import OTPLock from "@assets/svg/RRD/OTPLock";
import Ibutton from "@assets/svg/RRD/Ibutton";
import { actions as orderActions } from "@reducers/orderReducer";
import { actions as rrdActions, actions } from "@reducers/rrdReducer";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import * as Sentry from "@sentry/react-native";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import SearchIcon from "@assets/svg/SearchIcon";
import { filterData } from "@components/SearchHeader/utils";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import { DelayedTag, formatDate, ItemCard, Text } from "./Refund";
import { ShipmentTooltip } from "../MyDeliveries/DeliveryOrdersList";
import CustomModal from "../OrdersList/CustomModal";
import DeliveryInfoModal from "./DeliveryInfoModal";
import DelayDeliveryModal from "./DelayDeliveryModal";
import RescheduleModal from "./RescheduleModal";
import MetaStrip from "../../components/MetaStrip";

const PATH = "screens/RRD/DeliveryReturn";

const onPressCall = (item) => {
  const orders = item?.delivery?.map((i) => i?.order_id || "").join(",");
  handleCallButtonPress(item?.phone_number, RRD_EVENTS.MARKING_WIDGET_CLICKED, {
    type: "call",
    user_id: item?.user_id,
    user_name: item?.user_name,
    orders,
  });
};

const DeliveryReturn = ({ route }) => {
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [actionData, setActionData] = useState();
  const [deliveryInfoModalVisible, setDeliveryInfoModalVisible] =
    useState(false);
  const [delayDeliveryModalVisible, setDelayDeliveryModalVisible] =
    useState(false);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [rescheduleModalData, setRescheduleModalData] = useState();
  const [userId, setUserId] = useState(null);
  const [rescheduleOrderId, setRescheduleOrderId] = useState(null);
  const { overviewData, deliveryReturnData } = useSelector(
    (state) => state.rrd,
  );

  const dispatch = useDispatch();

  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        dispatch(actions.fetchRRDInfo());
      }
    }, [isFocused, dispatch]),
  );

  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };

    const backHandlerSub = BackHandler.addEventListener("hardwareBackPress", backHandler);

    return () => {
      backHandlerSub.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);

  useEffect(() => {
    setListData([
      {
        key: "delayedCustomers",
        data: deliveryReturnData?.delayedCustomers,
      },
      { key: "customers", data: deliveryReturnData?.customers },
      {
        key: "completedCustomers",
        data: deliveryReturnData?.completedCustomers,
      },
    ]);
    setIsLoading(false);
    setDeliveryInfoModalVisible(
      deliveryReturnData.delivery_info_modal.is_visible,
    );
  }, [deliveryReturnData]);

  const renderItem = useCallback(
    ({ item, section }) =>
      deliveryReturnData.isCxDeliveryRescheduleEnabled ? (
        <ReschedulingCard item={item} section={section} />
      ) : (
        <Card item={item} section={section} />
      ),
    [deliveryReturnData.isCxDeliveryRescheduleEnabled],
  );

  const reschedule = async (action) => {
    try {
      const response = await rescheduleDelivery(
        userId,
        rescheduleOrderId,
        action,
      );
      ToastMessage("info", response?.data?.message);
      dispatch(rrdActions.fetchRRDInfo());
    } catch (error) {
      console.log(error);
    }
  };

  const renderSection = ({ section: { key, data } }) => {
    if (data?.length === 0) return null;
    return (
      <View style={styles.sectionHeader}>
        <RRDStatus status={key} />
        <Text variant="subtitle_16" style={styles.sectionHeaderText}>
          {t(`${PATH}/${key}`, { count: deliveryReturnData?.tasksCount[key] })}
        </Text>
      </View>
    );
  };

  const keyExtractor = (item, index) => `${item.user_id}-${index}`;

  const handleOTPFlow = (response, action, data, item) => {
    if (response?.data.error === "OTP_VERIFICATION_FAILED") {
      navigation.navigate("OTPVerification", {
        action,
        id: data?.return_req_id ?? data?.order_id,
        data,
        otpLength: response?.data?.otpLength || 4,
        otpText: response?.data?.otpText || "",
      });
    } else if (response.status === 200) {
      dispatch(rrdActions.fetchRRDInfo());
      ToastMessage("success", response?.data?.toastMessage);
      handleBtnEvents(action, data, item);
    }
    setShowModal(false);
    setActionData({});
  };

  const handleBtnEvents = (action, data, item) => {
    const { return_req_id, order_id, delay_count_in_hours } = data;
    const { user_id, user_name, distance } = item;
    analytics.trackEvent(RRD_EVENTS.MARKING_PAGE_CTA_CLICKED, {
      return_req_id,
      order_id,
      delay_count_in_hours,
      user_id,
      user_name,
      distance,
      Marking_type: eventActionMapping[action],
      screen: "Mark delivery/return",
      card_type: action.includes("Delivery") ? "delivery" : "return",
    });
  };

  const onButtonPress = async (action, data, item) => {
    // if (!showModal && overviewData?.showModalsFor.includes(action)) {
    //   setActionData({ action, data, item });
    //   return setShowModal(true);
    // }
    if (action === "cancelDelivery") {
      return navigation.navigate("PreDeliveryItemList", {
        orders_list: [data?.order_id],
        spoke_name: "",
        date: "",
        user_name: item?.user_name,
        user_id: item?.user_id,
        user_phone: item?.phone_number,
        is_delivery: false,
      });
    }
    let response;
    try {
      if (action === "confirmPickup") {
        response = await markPickup(data?.return_req_id);
      } else if (action === "confirmCancelPickup") {
        response = await cancelPickupReq(data?.return_req_id);
      } else if (action === "unmarkPickup") {
        response = await unMarkPickup(data?.return_req_id);
      } else if (action === "confirmDelivery") {
        return navigation.navigate("PreDeliveryItemList", {
          orders_list: [data?.order_id],
          spoke_name: "",
          date: "",
          user_name: item?.user_name,
          user_id: item?.user_id,
          user_phone: item?.phone_number,
          is_delivery: true,
          afterOtpRedirectScreenName: "DeliveryReturn",
        });
      } else if (action === "confirmCancelReturnReq") {
        response = await returnRequestCancelV2({
          return_req_id: data?.return_req_id,
          type: "cx_rto",
        });
      } else if (action === "unmarkDelivery") {
        response = await unMarkDelivery(data?.order_id);
      }
    } catch (error) {
      setShowModal(false);
      setActionData({});
    }
    handleOTPFlow(response, action, data, item);
  };

  const openMaps = (latitude, longitude) => {
    try {
      Linking.openURL(
        Platform.OS === "ios"
          ? `https://maps.apple.com/maps?daddr=${latitude},${longitude}`
          : `https://maps.google.com/maps?q=loc:${latitude},${longitude}`,
      );
    } catch (error) {
      Alert.alert(t(`${PATH}/mapsAlert`));
      Sentry.captureException(error);
    }
  };

  const onDeliveryCardPress = (data, item) => {
    analytics.trackEvent(RRD_EVENTS.MARKING_CARD_CLICKED, {
      card_type: "delivery",
      user_id: item?.user_id,
      user_name: item?.user_name,
      order_id: data?.order_id,
      ticket_raised: data?.is_cx_complaint_raised,
      delay: data?.delay_count_in_hours,
    });
    dispatch(
      orderActions.setSelectedOrder({
        orderId: parseInt(data?.order_id, 10),
        source: "FORWARD",
      }),
    );
    navigation.navigate("OrderDetails");
  };

  const onPickupCardPress = (data, item) => {
    analytics.trackEvent(RRD_EVENTS.MARKING_CARD_CLICKED, {
      card_type: "return",
      user_id: item?.user_id,
      user_name: item?.user_name,
      return_req_id: data?.return_req_id,
      ticket_raised: data?.is_cx_complaint_raised,
      delay: data?.delay_count_in_hours,
    });
  };

  const ReschedulingCard = memo(({ item, section }) => {
    return (
      <View style={styles.listView}>
        <View style={styles.cardHeaderContainer}>
          {item?.delivery?.length &&
            item?.delivery.every(
              (v) => v.status === "PENDING_DELIVERY_TO_CX",
            ) && (
              <View style={styles.timeContainer}>
                {item.reschedule_tag && (
                  <View
                    style={[
                      styles.tagContainer,
                      { backgroundColor: item.reschedule_tag.backgroundColor },
                    ]}
                  >
                    <Dot fill="rgba(255, 255, 255, 1)" />
                    <Text variant="caption_2" style={styles.tag}>
                      {item.reschedule_tag.text}
                      {"   "}
                    </Text>
                    {item.time_remaining < 0 && (
                      <Pressable
                        onPress={() => setDelayDeliveryModalVisible(true)}
                      >
                        <Ibutton
                          CircleColor="rgba(255, 255, 255, 1)"
                          DotColor="rgba(0, 0, 0, 0.8)"
                          IColor="rgba(0, 0, 0, 0.8)"
                        />
                      </Pressable>
                    )}
                  </View>
                )}
                <View style={styles.timeShow}>
                  <Text
                    variant="body_text_3"
                    style={{ color: "rgba(0, 0, 0, 0.56)" }}
                  >
                    {item.time_remaining > 0
                      ? t(`${PATH}/remainingTime`)
                      : t(`${PATH}/delay`)}{" "}
                  </Text>
                  <DonutChart
                    total={item.total_time}
                    remaining={item.time_remaining}
                    radius={24}
                    strokeWidth={4}
                    text={t(`${PATH}/hours`)}
                  />
                </View>
              </View>
            )}
          <View style={styles.view1}>
            <View style={styles.nameContainer}>
              <Text variant="body_text_2" style={styles.name}>
                {item?.user_name}
              </Text>
              <View style={styles.dot} />
              <Text variant="body_text_3" style={styles.distance}>
                {item?.distance}
              </Text>
              <TouchableOpacity
                onPress={() => openMaps(item?.cx_lat, item?.cx_lng)}
              >
                <Image style={styles.locationIcon} source={Location} />
              </TouchableOpacity>
            </View>
            <TouchableOpacity onPress={() => onPressCall(item)}>
              <CallIcon strokeColor="#2195F2" width={17} height={17} />
            </TouchableOpacity>
          </View>
          <Text variant="body_text_3" style={styles.address}>
            {item?.address}
          </Text>
        </View>
        <View>
          {Math.abs(item?.pay_by_cx) > 0 && (
            <View>
              <View style={styles.dashedLine} />
              <View style={styles.amountOverviewContainer}>
                <Cash pay={Math.sign(item?.pay_by_cx) === 1} />
                <Text
                  variant="body_text_1"
                  style={{ paddingHorizontal: 8, lineHeight: 26 }}
                >
                  {t(
                    `${PATH}/${
                      Math.sign(item?.pay_by_cx) === 1 ? "pay_by_cx" : "pay_cx"
                    }`,
                  )}
                  <Text variant="body_text_2">{Math.abs(item?.pay_by_cx)}</Text>
                </Text>
              </View>
            </View>
          )}
          <View style={styles.horizontalSeparator} />
          {item?.pickups?.map((data, idx, arr) => (
            <TouchableWithoutFeedback
              key={`${data.count}-${data.amount}`}
              onPress={() => onPickupCardPress(data, item)}
            >
              <View
                style={{
                  backgroundColor:
                    arr.length > 1
                      ? idx % 2 === 0
                        ? "default"
                        : "#F5F5F5"
                      : "default",
                }}
              >
                <DelayedTag
                  section
                  type="pickup"
                  {...{ data, section: section.key, overviewData }}
                />
                <View style={styles.cardMiddleContainer}>
                  <View style={styles.view2}>
                    <View>
                      <Text variant="body_text_3" style={styles.text2}>
                        {t(`${PATH}/pickups/count`)}
                      </Text>
                      <Text variant="body_text_3" style={styles.text3}>
                        {data?.count}
                      </Text>
                    </View>
                    <View style={styles.separator} />
                    <View style={styles.cardItemSection}>
                      <View>
                        <Text variant="body_text_3" style={styles.text2}>
                          {t(`${PATH}/pickups/amount`)}
                        </Text>
                        <Text variant="body_text_3" style={styles.text3}>
                          {t(`${PATH}/rs`)}
                          {data?.amount}
                        </Text>
                      </View>
                      <PaymentMode
                        payment_tag={data?.payment_tag}
                        payment_mode={data?.payment_mode}
                      />
                    </View>
                  </View>
                </View>
                {data?.return_req_items?.map((item, index) => (
                  <ItemCard key={item.short_name ?? index} {...item} />
                ))}
                <View style={styles.bottomButtonContainer}>
                  {data?.status === "PENDING_PICKUP_FROM_CX" ? (
                    <View
                      style={[
                        styles.buttonContainer,
                        {
                          width: "50%",
                          alignSelf: "flex-end",
                        },
                      ]}
                    >
                      {/* <Button
                        title={t(`${PATH}/cancelPickup`)}
                        onPress={() => {
                          onButtonPress("confirmCancelPickup", data, item);
                        }}
                      /> */}
                      <Button
                        title={t(`${PATH}/markPickup`)}
                        btnColor={colors.plain_red}
                        onPress={() => {
                          onButtonPress("confirmPickup", data, item);
                        }}
                      />
                    </View>
                  ) : (
                    <View style={styles.unmarkContainer}>
                      <Image
                        style={styles.tickContainer}
                        source={{
                          uri: "https://public-cdn.citymall.live/cm-admin-storage-public/cl_app_icon%2F1668094182235-pickup.png",
                        }}
                      />
                      <View style={{ flex: 1 }}>
                        <View style={styles.unmarkTextContainer}>
                          <Text
                            newfont
                            variant="body_text_2"
                            style={{ color: colors.plain_red }}
                          >
                            {t(`${PATH}/pickedUp`)}
                          </Text>
                          {data?.can_unmark_pickup ? (
                            <TouchableOpacity
                              onPress={() =>
                                onButtonPress("unmarkPickup", data, item)
                              }
                            >
                              <Text
                                variant="caption_1"
                                style={styles.unmarkBtn}
                              >
                                {t(`${PATH}/unmark`)}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <Text
                          newfont
                          variant="caption_2"
                          style={styles.unmarkDate}
                        >
                          {formatDate(data?.picked_from_cx_at)}
                        </Text>
                      </View>
                    </View>
                  )}
                </View>
              </View>
            </TouchableWithoutFeedback>
          ))}
          {item?.delivery?.map((data, idx, arr) => (
            <TouchableNativeFeedback
              key={`${data.count}-${data.amount}`}
              background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
              onPress={() => onDeliveryCardPress(data, item)}
            >
              <View
                style={{
                  backgroundColor:
                    item?.pickups && item?.pickups.length % 2 !== 0
                      ? arr.length > 1
                        ? idx % 2 === 0
                          ? "#F5F5F5"
                          : "default"
                        : "#F5F5F5"
                      : arr.length > 1
                        ? idx % 2 === 0
                          ? "default"
                          : "#F5F5F5"
                        : "default",
                }}
              >
                <View style={styles.cardMiddleContainer}>
                  <View style={styles.view2}>
                    <View>
                      <Text variant="body_text_3" style={styles.text2}>
                        {t(`${PATH}/delivery/count`)}
                      </Text>
                      <View>
                        <ShipmentTooltip list={data?.shipments_list} />
                      </View>
                    </View>
                    <View style={styles.separator} />
                    <View style={styles.cardItemSection}>
                      <View>
                        <Text variant="body_text_3" style={styles.text2}>
                          {t(`${PATH}/delivery/amount`)}
                        </Text>
                        <Text variant="body_text_3" style={styles.text3}>
                          {t(`${PATH}/rs`)}
                          {data?.amount}
                        </Text>
                      </View>
                      <PaymentMode
                        payment_tag={data?.payment_tag}
                        payment_mode={data?.payment_mode}
                      />
                    </View>
                  </View>
                  <TouchableOpacity
                    onPress={() => onDeliveryCardPress(data, item)}
                    style={{ height: 24, width: 24, padding: 6 }}
                  >
                    <ChevronRight pathFill={colors.lightMediumBlack} />
                  </TouchableOpacity>
                </View>
                <View style={styles.bottomButtonContainer}>
                  {data?.status === "PENDING_DELIVERY_TO_CX" ? (
                    <>
                      <TouchableOpacity
                        onPress={async () => {
                          if (data?.is_otp_enabled) {
                            try {
                              const response = await markDelivery(
                                data?.order_id,
                              );
                              handleOTPFlow(
                                response,
                                "confirmDelivery",
                                data,
                                item,
                              );
                            } catch (e) {
                              console.log(e);
                            }
                          } else {
                            onButtonPress("confirmDelivery", data, item);
                          }
                        }}
                        style={[
                          styles.markDeliveryContainer,
                          data?.is_otp_enabled
                            ? styles.verifyOtp
                            : styles.markDelivery,
                        ]}
                      >
                        {data?.is_otp_enabled && <OTPLock />}
                        <Text
                          style={{ color: "white", fontWeight: "700" }}
                          newfont
                          variant="body_text_1"
                        >
                          {data?.is_otp_enabled
                            ? ` ${t(`${PATH}/verifyOTPButton`)}`
                            : t(`${PATH}/markDelivery`)}
                        </Text>
                      </TouchableOpacity>
                      <View style={styles.buttonContainer}>
                        <TouchableOpacity
                          onPress={() => {
                            if (!data.can_reschedule) {
                              ToastMessage(
                                "info",
                                t(`${PATH}/notReschedulable`),
                              );
                              return;
                            }
                            setRescheduleModalData({
                              ...deliveryReturnData.reschedule_info_modal,
                              question:
                                deliveryReturnData.reschedule_info_modal?.question.replace(
                                  "$$name",
                                  item.user_name,
                                ),
                            });
                            setRescheduleModalVisible(true);
                            setUserId(item.user_id);
                            setRescheduleOrderId(data.order_id);
                          }}
                          style={styles.reschedule}
                        >
                          <Clock
                            fill={
                              data.can_reschedule
                                ? "rgba(0, 0, 0, 0.56)"
                                : "rgba(0, 0, 0, 0.36)"
                            }
                          />
                          <Text
                            style={{
                              color: data.can_reschedule
                                ? "rgba(0, 0, 0, 0.56)"
                                : "rgba(0, 0, 0, 0.36)",
                            }}
                            newfont
                            variant="body_text_4"
                          >
                            {" "}
                            {t(`${PATH}/reschedule`)}
                          </Text>
                        </TouchableOpacity>
                        <View style={styles.verticalLine} />
                        <TouchableOpacity
                          onPress={() => onButtonPress("cancelDelivery", data)}
                          style={styles.rto}
                        >
                          <Backward />
                          <Text
                            style={{ color: "rgba(0, 0, 0, 0.56)" }}
                            newfont
                            variant="body_text_4"
                          >
                            {" "}
                            {t(`${PATH}/cancelDelivery`)}
                          </Text>
                        </TouchableOpacity>
                      </View>
                      {/* {data?.is_otp_enabled && (
                        <Text variant="caption_2" style={styles.verifyOtpText}>
                          {t(`${PATH}/verifyOTP`)}
                        </Text>
                      )} */}
                    </>
                  ) : data?.status === "MARKED_FULL_RTO" ? (
                    <View style={styles.unmarkContainer}>
                      <Image
                        style={styles.tickContainer}
                        source={{
                          uri: "https://public-cdn.citymall.live/cm-admin-storage-public/cl_app_icon%2F1668094346429-Returned%20order.png",
                        }}
                      />
                      <View style={{ flex: 1 }}>
                        <View style={styles.unmarkTextContainer}>
                          <Text
                            newfont
                            variant="body_text_2"
                            style={{ color: "#2F80ED" }}
                          >
                            {t(`${PATH}/returned`)}
                          </Text>
                          {data?.can_unmark_cx_rto ? (
                            <TouchableOpacity
                              onPress={() =>
                                onButtonPress(
                                  "confirmCancelReturnReq",
                                  data,
                                  item,
                                )
                              }
                            >
                              <Text
                                variant="caption_1"
                                style={styles.unmarkBtn}
                              >
                                {t(`${PATH}/unmark`)}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <Text
                          newfont
                          variant="caption_2"
                          style={styles.unmarkDate}
                        >
                          {formatDate(data?.cx_rto_raised_at)}
                        </Text>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.unmarkContainer}>
                      <Image
                        style={styles.tickContainer}
                        source={{
                          uri: "https://public-cdn.citymall.live/cm-admin-storage-public/cl_app_icon%2F1668094559360-Tick.png",
                        }}
                      />
                      <View style={{ flex: 1 }}>
                        <View style={styles.unmarkTextContainer}>
                          <Text
                            newfont
                            variant="body_text_2"
                            style={{ color: colors.plain_green }}
                          >
                            {t(`${PATH}/delivered`)}
                          </Text>
                          {data.can_unmark_delivery ? (
                            <TouchableOpacity
                              onPress={() =>
                                onButtonPress("unmarkDelivery", data, item)
                              }
                            >
                              <Text
                                variant="caption_1"
                                style={styles.unmarkBtn}
                              >
                                {t(`${PATH}/unmark`)}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <Text
                          newfont
                          variant="caption_2"
                          style={styles.unmarkDate}
                        >
                          {formatDate(data?.delivered_to_cx_at)}
                        </Text>
                      </View>
                    </View>
                  )}
                  {data?.extra_commission_strip ? (
                    <MetaStrip
                      text={item.extra_commission_strip}
                      smallStrip
                      extraStyling={{ marginTop: 0 }}
                    />
                  ) : null}
                </View>
              </View>
            </TouchableNativeFeedback>
          ))}
        </View>
      </View>
    );
  });

  const Card = memo(({ item, section }) => {
    return (
      <View style={styles.listView}>
        <View style={styles.cardHeaderContainer}>
          <View style={styles.view1}>
            <View style={styles.nameContainer}>
              <Text variant="body_text_2" style={styles.name}>
                {item?.user_name}
              </Text>
              <View style={styles.dot} />
              <Text variant="body_text_3" style={styles.distance}>
                {item?.distance}
              </Text>
              <TouchableOpacity
                onPress={() => openMaps(item?.cx_lat, item?.cx_lng)}
              >
                <Image style={styles.locationIcon} source={Location} />
              </TouchableOpacity>
            </View>
            <TouchableOpacity onPress={() => onPressCall(item)}>
              <CallIcon strokeColor="#2195F2" width={17} height={17} />
            </TouchableOpacity>
          </View>
          <Text variant="body_text_3" style={styles.address}>
            {item?.address}
          </Text>
        </View>
        <View>
          {Math.abs(item?.pay_by_cx) > 0 && (
            <View>
              <View style={styles.dashedLine} />
              <View style={styles.amountOverviewContainer}>
                <Cash pay={Math.sign(item?.pay_by_cx) === 1} />
                <Text
                  variant="body_text_1"
                  style={{ paddingHorizontal: 8, lineHeight: 26 }}
                >
                  {t(
                    `${PATH}/${
                      Math.sign(item?.pay_by_cx) === 1 ? "pay_by_cx" : "pay_cx"
                    }`,
                  )}
                  <Text variant="body_text_2">{Math.abs(item?.pay_by_cx)}</Text>
                </Text>
              </View>
            </View>
          )}
          <View style={styles.horizontalSeparator} />
          {item?.pickups?.map((data, idx, arr) => (
            <TouchableWithoutFeedback
              key={`${data.count}-${data.amount}`}
              onPress={() => onPickupCardPress(data, item)}
            >
              <View
                style={{
                  backgroundColor:
                    arr.length > 1
                      ? idx % 2 === 0
                        ? "default"
                        : "#F5F5F5"
                      : "default",
                }}
              >
                <DelayedTag
                  section
                  type="pickup"
                  {...{ data, section: section.key, overviewData }}
                />
                <View style={styles.cardMiddleContainer}>
                  <View style={styles.view2}>
                    <View>
                      <Text variant="body_text_3" style={styles.text2}>
                        {t(`${PATH}/pickups/count`)}
                      </Text>
                      <Text variant="body_text_3" style={styles.text3}>
                        {data?.count}
                      </Text>
                    </View>
                    <View style={styles.separator} />
                    <View style={styles.cardItemSection}>
                      <View>
                        <Text variant="body_text_3" style={styles.text2}>
                          {t(`${PATH}/pickups/amount`)}
                        </Text>
                        <Text variant="body_text_3" style={styles.text3}>
                          {t(`${PATH}/rs`)}
                          {data?.amount}
                        </Text>
                      </View>
                      <PaymentMode
                        payment_tag={data?.payment_tag}
                        payment_mode={data?.payment_mode}
                      />
                    </View>
                  </View>
                </View>
                {data?.return_req_items?.map((item, index) => (
                  <ItemCard key={item.short_name ?? index} {...item} />
                ))}
                <View style={styles.bottomButtonContainer}>
                  {data?.status === "PENDING_PICKUP_FROM_CX" ? (
                    <View
                      style={[
                        styles.buttonContainer,
                        {
                          width: "50%",
                          alignSelf: "flex-end",
                        },
                      ]}
                    >
                      {/* <Button
                        title={t(`${PATH}/cancelPickup`)}
                        onPress={() => {
                          onButtonPress("confirmCancelPickup", data, item);
                        }}
                      /> */}
                      <Button
                        title={t(`${PATH}/markPickup`)}
                        btnColor={colors.plain_red}
                        onPress={() => {
                          onButtonPress("confirmPickup", data, item);
                        }}
                      />
                    </View>
                  ) : (
                    <View style={styles.unmarkContainer}>
                      <Image
                        style={styles.tickContainer}
                        source={{
                          uri: "https://public-cdn.citymall.live/cm-admin-storage-public/cl_app_icon%2F1668094182235-pickup.png",
                        }}
                      />
                      <View style={{ flex: 1 }}>
                        <View style={styles.unmarkTextContainer}>
                          <Text
                            newfont
                            variant="body_text_2"
                            style={{ color: colors.plain_red }}
                          >
                            {t(`${PATH}/pickedUp`)}
                          </Text>
                          {data?.can_unmark_pickup ? (
                            <TouchableOpacity
                              onPress={() =>
                                onButtonPress("unmarkPickup", data, item)
                              }
                            >
                              <Text
                                variant="caption_1"
                                style={styles.unmarkBtn}
                              >
                                {t(`${PATH}/unmark`)}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <Text
                          newfont
                          variant="caption_2"
                          style={styles.unmarkDate}
                        >
                          {formatDate(data?.picked_from_cx_at)}
                        </Text>
                      </View>
                    </View>
                  )}
                </View>
              </View>
            </TouchableWithoutFeedback>
          ))}
          {item?.delivery?.map((data, idx, arr) => (
            <TouchableNativeFeedback
              key={`${data.count}-${data.amount}`}
              background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
              onPress={() => onDeliveryCardPress(data, item)}
            >
              <View
                style={{
                  backgroundColor:
                    item?.pickups && item?.pickups.length % 2 !== 0
                      ? arr.length > 1
                        ? idx % 2 === 0
                          ? "#F5F5F5"
                          : "default"
                        : "#F5F5F5"
                      : arr.length > 1
                        ? idx % 2 === 0
                          ? "default"
                          : "#F5F5F5"
                        : "default",
                }}
              >
                <DelayedTag
                  type="delivery"
                  {...{ data, section: section.key, overviewData }}
                />
                <View style={styles.cardMiddleContainer}>
                  <View style={styles.view2}>
                    {data?.shipments_list && data?.shipments_list.length ? (
                      <View>
                        <Text variant="body_text_3" style={styles.text2}>
                          {t(`${PATH}/delivery/count`)}
                        </Text>
                        <ShipmentTooltip list={data?.shipments_list} />
                      </View>
                    ) : data?.order_items_count ? (
                      <View>
                        <Text variant="body_text_3" style={styles.text2}>
                          {t(`${PATH}/delivery/items`)}
                        </Text>
                        <Text variant="body_text_4" style={styles.text3}>
                          {data?.order_items_count}
                        </Text>
                      </View>
                    ) : null}
                    <View style={styles.separator} />
                    <View style={styles.cardItemSection}>
                      <View>
                        <Text variant="body_text_3" style={styles.text2}>
                          {t(`${PATH}/delivery/amount`)}
                        </Text>
                        <Text variant="body_text_3" style={styles.text3}>
                          {t(`${PATH}/rs`)}
                          {data?.amount}
                        </Text>
                      </View>
                      <PaymentMode
                        payment_tag={data?.payment_tag}
                        payment_mode={data?.payment_mode}
                      />
                    </View>
                  </View>
                  <TouchableOpacity
                    onPress={() => onDeliveryCardPress(data, item)}
                    style={{ height: 24, width: 24, padding: 6 }}
                  >
                    <ChevronRight pathFill={colors.lightMediumBlack} />
                  </TouchableOpacity>
                </View>
                <View style={styles.bottomButtonContainer}>
                  {data?.status === "PENDING_DELIVERY_TO_CX" ? (
                    <>
                      <View style={styles.buttonContainer}>
                        <Button
                          title={t(`${PATH}/cancelDelivery`)}
                          onPress={() =>
                            onButtonPress("cancelDelivery", data, item)
                          }
                        />
                        <Button
                          title={t(`${PATH}/markDelivery`)}
                          btnColor={colors.plain_green}
                          onPress={() =>
                            onButtonPress("confirmDelivery", data, item)
                          }
                        />
                      </View>
                      {data?.is_otp_enabled && (
                        <Text variant="caption_2" style={styles.verifyOtpText}>
                          {t(`${PATH}/verifyOTP`)}
                        </Text>
                      )}
                    </>
                  ) : data?.status === "MARKED_FULL_RTO" ? (
                    <View style={styles.unmarkContainer}>
                      <Image
                        style={styles.tickContainer}
                        source={{
                          uri: "https://public-cdn.citymall.live/cm-admin-storage-public/cl_app_icon%2F1668094346429-Returned%20order.png",
                        }}
                      />
                      <View style={{ flex: 1 }}>
                        <View style={styles.unmarkTextContainer}>
                          <Text
                            newfont
                            variant="body_text_2"
                            style={{ color: "#2F80ED" }}
                          >
                            {t(`${PATH}/returned`)}
                          </Text>
                          {data?.can_unmark_cx_rto ? (
                            <TouchableOpacity
                              onPress={() =>
                                onButtonPress(
                                  "confirmCancelReturnReq",
                                  data,
                                  item,
                                )
                              }
                            >
                              <Text
                                variant="caption_1"
                                style={styles.unmarkBtn}
                              >
                                {t(`${PATH}/unmark`)}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <Text
                          newfont
                          variant="caption_2"
                          style={styles.unmarkDate}
                        >
                          {formatDate(data?.cx_rto_raised_at)}
                        </Text>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.unmarkContainer}>
                      <Image
                        style={styles.tickContainer}
                        source={{
                          uri: "https://public-cdn.citymall.live/cm-admin-storage-public/cl_app_icon%2F1668094559360-Tick.png",
                        }}
                      />
                      <View style={{ flex: 1 }}>
                        <View style={styles.unmarkTextContainer}>
                          <Text
                            newfont
                            variant="body_text_2"
                            style={{ color: colors.plain_green }}
                          >
                            {t(`${PATH}/delivered`)}
                          </Text>
                          {data?.can_unmark_delivery ? (
                            <TouchableOpacity
                              onPress={() =>
                                onButtonPress("unmarkDelivery", data, item)
                              }
                            >
                              <Text
                                variant="caption_1"
                                style={styles.unmarkBtn}
                              >
                                {t(`${PATH}/unmark`)}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <Text
                          newfont
                          variant="caption_2"
                          style={styles.unmarkDate}
                        >
                          {formatDate(data?.delivered_to_cx_at)}
                        </Text>
                      </View>
                    </View>
                  )}
                  {data?.extra_commission_strip ? (
                    <MetaStrip
                      text={data.extra_commission_strip}
                      smallStrip
                      extraStyling={{ marginHorizontal: 4 }}
                    />
                  ) : null}
                </View>
              </View>
            </TouchableNativeFeedback>
          ))}
        </View>
      </View>
    );
  });

  const onSearchInputChange = debounce((text) => {
    const extraFilters = [
      // eslint-disable-next-line no-shadow
      (item, text) =>
        item?.delivery?.length > 0 &&
        item?.delivery?.some((deliveryItem) =>
          deliveryItem?.order_id
            ?.toLowerCase()
            ?.includes(text?.trim().toLowerCase()),
        ),
      // eslint-disable-next-line no-shadow
      (item, text) =>
        item?.pickups?.length > 0 &&
        item?.pickups?.some((pickupItem) =>
          pickupItem?.order_id
            ?.toLowerCase()
            ?.includes(text?.trim().toLowerCase()),
        ),
    ];
    setListData([
      {
        key: "delayedCustomers",
        data: filterData(
          text,
          deliveryReturnData?.delayedCustomers,
          extraFilters,
        ),
      },
      {
        key: "customers",
        data: filterData(text, deliveryReturnData?.customers, extraFilters),
      },
      {
        key: "completedCustomers",
        data: filterData(
          text,
          deliveryReturnData?.completedCustomers,
          extraFilters,
        ),
      },
    ]);
  }, 500);

  const onPressCrossIcon = () => {
    onSearchInputChange("");
    analytics.trackEvent(SEARCH_EVENTS.SEARCH_CROSS_CLICKED, {
      screen_name: "DeliveryReturn",
    });
  };
  const onPressSearchIcon = () => {
    setIsSearchEnabled(!isSearchEnabled);
    analytics.trackEvent(SEARCH_EVENTS.SEARCH_CLICKED, {
      screen_name: "DeliveryReturn",
    });
  };
  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };

  const checkListDataIsEmpty = () => {
    if (listData) {
      return listData?.every((item) => item?.data?.length === 0);
    }
    return true;
  };

  return (
    <View style={styles.container}>
      <HeaderWithSearch
        headertitle={
          !isSearchEnabled ? t(`${PATH}/heading`) : t(`SearchOrder/title`)
        }
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        searchIcon={<SearchIcon height={20} width={20} />}
        isSearchEnabled={isSearchEnabled}
        searchPlaceholder={t(`SearchOrder/placeholder`)}
        onPressCrossIcon={onPressCrossIcon}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        onSearchInputChange={onSearchInputChange}
        onPressSearchIcon={onPressSearchIcon}
      />
      <CustomModal
        heading={deliveryReturnData?.popups[actionData?.action]?.heading ?? ""}
        message={deliveryReturnData?.popups[actionData?.action]?.message ?? ""}
        buttons={deliveryReturnData?.popups[actionData?.action]?.buttons}
        visible={showModal}
        onPress={() =>
          onButtonPress(actionData?.action, actionData?.data, actionData?.item)
        }
        fontFamily={false}
        headingFontWeight="bold"
        headingFontSize={16}
        messageFontSize={14}
        setShowModal={() => setShowModal(false)}
      />
      <DeliveryInfoModal
        visible={deliveryInfoModalVisible}
        setVisible={setDeliveryInfoModalVisible}
        info={deliveryReturnData.delivery_info_modal}
      />
      <DelayDeliveryModal
        visible={delayDeliveryModalVisible}
        setVisible={setDelayDeliveryModalVisible}
        info={deliveryReturnData.delay_delivery_modal}
      />
      <RescheduleModal
        visible={rescheduleModalVisible}
        setVisible={setRescheduleModalVisible}
        onSubmit={reschedule}
        info={rescheduleModalData}
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : checkListDataIsEmpty() ? (
        <EmptySearchList />
      ) : (
        <SectionList
          renderSectionHeader={renderSection}
          keyExtractor={keyExtractor}
          sections={listData}
          renderItem={renderItem}
          ListHeaderComponent={Carousel(deliveryReturnData?.carousel)}
          keyboardShouldPersistTaps="handled"
        />
      )}
    </View>
  );
};

const PaymentMode = ({ payment_mode, payment_tag }) => {
  return (
    <View style={styles.paymentTag}>
      <Tick visible={payment_mode === "PREPAID"} />
      <Text
        variant="caption_3"
        style={[
          styles.paymentTagText,
          {
            color:
              payment_mode === "PREPAID"
                ? colors.lightBlack
                : colors.plain_green,
            marginLeft: payment_mode === "PREPAID" ? 4 : 0,
          },
        ]}
      >
        {payment_tag}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  bottomButtonContainer: {
    marginVertical: 16,
    paddingHorizontal: 16,
  },
  buttonContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  listView: {
    backgroundColor: colors.white,
    borderRadius: 10,
    elevation: 4,
    overflow: "hidden",
    marginHorizontal: 16,
    marginVertical: 8,
  },
  view1: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  view2: {
    flexDirection: "row",
    alignItems: "center",
  },
  text2: {
    color: "rgba(0, 0, 0, 0.56)",
  },
  text3: {
    fontFamily: "NotoSansDevanagari-Bold",
  },
  name: {
    paddingHorizontal: 2,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  sectionHeaderText: {
    marginHorizontal: 6,
  },
  paymentTag: {
    borderRadius: 20,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: "center",
    marginHorizontal: 8,
    justifyContent: "center",
    borderColor: colors.lighterBlack,
    flexDirection: "row",
    backgroundColor: colors.white,
  },
  distance: { color: colors.lightBlack, marginRight: 4, fontWeight: "500" },
  paymentTagText: {
    color: colors.plain_green,
    fontWeight: "bold",
  },
  address: {
    // maxWidth: "80%",
    color: colors.lightBlack,
  },
  separator: {
    backgroundColor: colors.lighterBlack,
    height: 24,
    width: 1,
    marginLeft: 12,
    marginRight: 24,
  },
  horizontalSeparator: {
    backgroundColor: colors.lighterBlack,
    height: 1,
    width: "100%",
    // marginBottom: 16,
    alignSelf: "center",
  },
  cardItemSection: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
  },
  cardHeaderContainer: { padding: 16 },
  dashedLine: {
    flex: 1,
    opacity: 0.7,
    height: 1,
    borderWidth: 0.5,
    borderColor: colors.extraLightBlack,
    borderStyle: "dashed",
    borderRadius: 1,
  },
  nameContainer: { flexDirection: "row" },
  locationIcon: { height: 16, width: 16 },
  amountOverviewContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  verifyOtpText: { alignSelf: "flex-end", marginTop: 8 },
  dot: {
    height: 4,
    width: 4,
    borderRadius: 2,
    backgroundColor: colors.lightBlack,
    margin: 8,
  },
  cardMiddleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    marginTop: 16,
    marginBottom: 8,
  },
  unmarkContainer: { width: "100%", flexDirection: "row" },
  unmarkTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  unmarkBtn: { textDecorationLine: "underline", color: colors.lightBlack },
  unmarkDate: { color: colors.lightBlack },
  tickContainer: {
    height: 20,
    width: 20,
    marginRight: 8,
  },
  timeContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },
  tagContainer: {
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    height: 24,
  },
  tag: { color: "rgba(255, 255, 255, 1)", fontWeight: "700" },
  timeShow: { flexDirection: "row", alignItems: "center" },
  markDeliveryContainer: {
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    flexDirection: "row",
  },
  markDelivery: {
    backgroundColor: "rgba(0, 171, 89, 1)",
  },
  verifyOtp: {
    backgroundColor: "rgba(0, 120, 218, 1)",
  },
  reschedule: {
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    width: "49%",
    flexDirection: "row",
  },
  verticalLine: {
    backgroundColor: colors.lighterBlack,
    height: 24,
    width: 1,
  },
  rto: {
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    width: "49%",
    flexDirection: "row",
  },
});

export default DeliveryReturn;
